import { useState } from 'react'

import { DateTime } from 'luxon'

import { SimpleTable, TableSkeleton, Alert, AssetDetailModal } from '@/components/ui'
import useCompany from '@/hooks/useCompany'
import { buildQuery } from '@/insights/common/queryUtils'
import Caption, { addDateToCaptionFields } from '@/insights/components/Caption'
import LiveDuration from '@/insights/components/LiveDuration'
import {
  ApiService,
  type InsightDefinition,
  InsightParametersType,
  InsightStatusCode,
  isInsightStatus,
} from '@/types/dashboard-types'
import { downloadCsv } from '@/utils/csvExport'
import { formatUserDate } from '@/utils/formatDate'
import { formatDurationLabel, formatDurationToHours } from '@/utils/formatting'

export const DurationOptions = [
  { label: '3 Days', value: '3d' },
  { label: '7 Days', value: '7d' },
  { label: '30 Days', value: '30d', isDefault: true },
  { label: 'Custom', value: 'custom' },
]

export const DurationUnits = [
  { label: 'Hours', value: 'h' },
  { label: 'Days', value: 'd', isDefault: true },
]

export interface StationaryAssetsDetailParams extends InsightParametersType {
  companyId?: number
  geofenceIds?: number[]
  geofenceCategoryIds?: number[]
  minDuration: string
  customMinDuration?: string
  topN?: string
}

export interface StationaryAssetData {
  companyName: string
  companyId: number
  assetName: string
  assetId: number
  geofenceName: string
  geofenceEntryTime: Date
  gatewayOn: boolean
  gatewayOfflineSince: Date | null
}

const formatGatewayStatus = (item: StationaryAssetData) => {
  if (item.gatewayOn) {
    return 'Online'
  }
  return item.gatewayOfflineSince ? `Offline since ${formatUserDate(item.gatewayOfflineSince)}` : 'Offline'
}

const withMinimumDuration = (parameters: StationaryAssetsDetailParams) => ({
  ...parameters,
  minimumDuration: formatDurationToHours(
    (parameters.minDuration === 'custom' ? parameters.customMinDuration : parameters.minDuration) ?? ''
  ),
})

const StationaryAssetsDetail: InsightDefinition<StationaryAssetsDetailParams, StationaryAssetData[]> = {
  active: true,
  slug: 'utilization/stationary-assets-detail',
  category: 'utilization',
  name: 'Stationary Assets Detail',
  description: 'List stationary assets details',
  defaultWidth: 'medium',
  defaultAspectRatio: 16 / 9,

  configuration: {
    transform: ({ parameters, field }) => {
      if (field === 'geofenceIds') {
        return { ...parameters, geofenceCategoryIds: undefined }
      }
      if (field === 'geofenceCategoryIds') {
        return { ...parameters, geofenceIds: undefined }
      }
      if (field === 'minDuration' && parameters.minDuration !== 'custom') {
        return { ...parameters, customMinDuration: undefined }
      }
      return parameters
    },
    controls: [
      { id: 'companyId', control: 'CompanyDropdown', required: true },
      { id: 'note', control: 'Label', label: 'Choose either Geofence or Geofence Category' },
      { id: 'geofenceIds', control: 'GeofenceDropdown', required: false },
      { id: 'geofenceCategoryIds', control: 'GeofenceCategoryDropdown', required: false },
      {
        id: 'minDuration',
        label: 'Minimum Duration',
        control: 'CustomSingleSelect',
        options: DurationOptions,
      },
      {
        id: 'customMinDuration',
        label: 'Enter duration',
        control: 'CustomNumberInput',
        dependsOn: 'minDuration',
        dependsOnValue: 'custom',
        defaultValue: 30,
        // max: 30,
        options: DurationUnits,
      },
      {
        id: 'topN',
        label: 'Top',
        control: 'CustomSingleSelect',
        options: [
          { label: 'All', value: '' },
          { label: 'Top 5', value: '5' },
          { label: 'Top 10', value: '10' },
          { label: 'Top 20', value: '20' },
        ],
      },
    ],

    isValid: (configuration: StationaryAssetsDetailParams) => {
      if (!configuration.companyId) {
        return 'Company is required.'
      }

      const hasGeofences = Array.isArray(configuration.geofenceIds) && configuration.geofenceIds.length > 0
      const hasCategories =
        Array.isArray(configuration.geofenceCategoryIds) && configuration.geofenceCategoryIds.length > 0

      if (!hasGeofences && !hasCategories) {
        return 'Please select at least one Geofence or Geofence Category.'
      }

      if (configuration.minDuration === 'custom') {
        const customMinDuration = parseInt(configuration.customMinDuration ?? '0', 10)

        if (!customMinDuration || customMinDuration === 0) {
          return 'minimum duration is required.'
        }
        if (customMinDuration > 30) {
          return 'minimum duration cannot exceed 30 days.'
        }
      }

      return true
    },
  },
  csv: async (api: ApiService, parameters: StationaryAssetsDetailParams, title: string) => {
    const path = '/insights/utilization/stationary-asset-detail'
    await downloadCsv(api, path, withMinimumDuration(parameters), title)
  },
  load: async (api: ApiService, rawParameters: StationaryAssetsDetailParams) => {
    const parameters = withMinimumDuration(rawParameters)

    const path = `/insights/utilization/stationary-asset-detail?${buildQuery(parameters)}`
    const response = await api.get<StationaryAssetData[]>({
      path,
      origin: 'StationaryAssetDetail#loader',
    })
    if (response.error) {
      return { code: InsightStatusCode.ERROR, message: response.message }
    }
    if (!response.data) {
      return { code: InsightStatusCode.NO_DATA }
    }
    return response.data
  },

  Display: ({ status, data, configuration }) => {
    const [selectedAsset, setSelectedAsset] = useState<number | undefined>()
    if (status.code === InsightStatusCode.NOT_CONFIGURED) {
      return <Alert type="info" message={status.message || 'Insight has not been configured'} />
    }
    if (status.code === InsightStatusCode.LOADING) {
      // This could be handled by the framework by just returning the
      // same status. But we should make the skeleton match the final
      // table result if possible.
      return <TableSkeleton columns={6} withHeader rowHeight={35} />
    }
    if (status.code !== InsightStatusCode.DISPLAYING) {
      // Wait until we're ready to display before displaying
      // Framework will handle the other states like error and no data.
      // We could handle no data here with a custom message like
      // "No alarms found for severity critical".
      return null
    }
    if (isInsightStatus(data)) {
      return null
    }
    if (!data?.length) {
      // Probably would never get here, but keeps typescript happy.
      return null
    }
    const parameters = configuration.parameters as StationaryAssetsDetailParams
    const configuredCompanyId = parameters.companyId

    const uniqueCompanies = new Set(data.map((item) => item.companyId))
    const shouldShowCompany =
      uniqueCompanies.size > 1 ||
      (configuredCompanyId && uniqueCompanies.size === 1 && !uniqueCompanies.has(configuredCompanyId))

    const body = data
      .sort((a, b) => {
        const t = a.geofenceEntryTime.getTime() - b.geofenceEntryTime.getTime()
        if (t === 0) {
          return a.assetName.localeCompare(b.assetName)
        }
        return t
      })
      .map((item) => ({
        ...(shouldShowCompany && {
          Company: <span>{item.companyName}</span>,
        }),
        'Asset Name': (
          <button
            onClick={() => setSelectedAsset(item.assetId)}
            className="text-left block text-blue-500 cursor-pointer"
          >
            {item.assetName}
          </button>
        ),
        'Gateway Status': {
          value: item.gatewayOn ? 0 : (item.gatewayOfflineSince?.getTime() ?? Number.MAX_SAFE_INTEGER),
          display: <span className="block text-left">{formatGatewayStatus(item)}</span>,
        },
        Geofence: <span className="block text-left">{item.geofenceName}</span>,
        Duration: {
          value: new Date().getTime() - item.geofenceEntryTime.getTime(),
          display: (
            <LiveDuration
              raisedTime={item.geofenceEntryTime}
              clearedTime={undefined}
              className="block text-left min-w-fit"
            />
          ),
        },
        'Last Geofence Entry': {
          id: `entry-${item.assetId}-${item.geofenceName}`,
          date: new Date(item.geofenceEntryTime),
          display: DateTime.fromJSDate(new Date(item.geofenceEntryTime)).toFormat('yyyy-MM-dd HH:mm'),
        },
      }))

    const dataItem = body?.[0]

    const headers = dataItem
      ? Object.keys(dataItem)
      : Array(3)
          .fill(null)
          .map((_, index) => `placeholder-${index}`)

    return (
      <section className="text-base-content text-base h-full flex flex-col">
        <div className="min-h-0 flex-1">
          <SimpleTable headers={headers} body={body} />
        </div>
        <AssetDetailModal
          show={selectedAsset !== undefined}
          onHide={() => setSelectedAsset(undefined)}
          assetId={selectedAsset}
          companyId={parameters.companyId}
        />
      </section>
    )
  },

  Caption: ({ configuration }) => {
    const { companies } = useCompany()
    const fields: Record<string, string> = {}
    const params = configuration.parameters as StationaryAssetsDetailParams

    if (params.companyId) {
      const company = companies.find((c) => c.id === params.companyId)
      if (company) {
        fields['Company'] = company.name
      }
    }
    if (params.minDuration) {
      const duration = params.minDuration === 'custom' ? params.customMinDuration : params.minDuration
      fields['Minimum Duration'] = formatDurationLabel(duration || '')
    }
    addDateToCaptionFields({ ...params, fields, finite: true })
    if (Object.keys(fields).length) {
      return <Caption fields={fields} />
    }
    return ''
  },

  // Could leave this off and the framework should handle it;
  // this is just an illustration of how to do it.
  Footer: ({ data, status }) => {
    if (status.code === InsightStatusCode.LOADING) {
      return ''
    }
    if (status.code === InsightStatusCode.NO_DATA) {
      return <div>No records found</div>
    }
    const rows = data?.length
    if (!rows) {
      return <div>No records found</div>
    }
    return (
      <div>
        {rows} row{rows === 1 ? '' : 's'} found
      </div>
    )
  },
}

export default StationaryAssetsDetail
